import {
  CREW_MARKER_COLORS,
  crewMarkerGeometry,
  crewOwnColor,
  getInitials,
  ringDash,
} from "@produce/shared";

interface CrewMapAvatarProps {
  name: string;
  /** Signed profile photo URL, when the crew member uploaded one. */
  photoUrl?: string | null;
  /** The crew member's personal colour. */
  color?: string | null;
  /**
   * Progress of their current assignment (0–1), or null when they have
   * none — the ring then stays an empty grey track.
   */
  progress?: number | null;
  size?: number;
  className?: string;
}

/**
 * The map marker, rendered in React for lists rather than on a map.
 *
 * The crew list beside the producer's live-tracking map had no avatar at
 * all, which made it impossible to match a name to the dot you were
 * looking at. This draws the exact same thing — same rings, same
 * geometry, same colours out of @produce/shared — so the two line up.
 *
 * The mapbox markers themselves can't use this: they need a detached DOM
 * node, which is what lib/crewMarkerElement builds.
 */
export const CrewMapAvatar = ({
  name,
  photoUrl,
  color,
  progress = null,
  size = 32,
  className,
}: CrewMapAvatarProps) => {
  const g = crewMarkerGeometry(size);
  const { circumference, dashOffset } = ringDash(g.progressRadius, progress ?? 0);
  const ownColor = crewOwnColor(color);

  return (
    <div
      className={className}
      style={{ position: "relative", width: g.size, height: g.size, flexShrink: 0 }}
    >
      <svg width={g.size} height={g.size} viewBox={`0 0 ${g.size} ${g.size}`} aria-hidden="true">
        <circle
          cx={g.center}
          cy={g.center}
          r={g.progressRadius}
          fill="none"
          stroke={CREW_MARKER_COLORS.track}
          strokeWidth={g.progressWidth}
        />
        {progress != null && (
          <circle
            cx={g.center}
            cy={g.center}
            r={g.progressRadius}
            fill="none"
            stroke={CREW_MARKER_COLORS.progress}
            strokeWidth={g.progressWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${g.center} ${g.center})`}
          />
        )}
        <circle
          cx={g.center}
          cy={g.center}
          r={g.ownRadius}
          fill="none"
          stroke={ownColor}
          strokeWidth={g.ownWidth}
        />
        <circle cx={g.center} cy={g.center} r={g.discRadius} fill={CREW_MARKER_COLORS.disc} />
      </svg>

      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          style={{
            position: "absolute",
            top: g.photoOffset,
            left: g.photoOffset,
            width: g.photoSize,
            height: g.photoSize,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ) : (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: CREW_MARKER_COLORS.label,
            fontWeight: 700,
            fontSize: g.fontSize,
            lineHeight: 1,
          }}
        >
          {getInitials(name)}
        </span>
      )}
    </div>
  );
};
